import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Copy, Upload, ArrowRight, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fetchCourses } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const EDUCATION = ["High school", "Diploma", "Bachelor's degree", "Master's degree", "PhD", "Other"];
const EXPERIENCE = ["Student / Fresher", "0–2 yrs IT experience", "3–7 yrs IT experience", "8+ yrs IT experience", "Non-IT professional (career switcher)"];

function upiIntentUrl({ pa, pn, am }) {
  const q = new URLSearchParams({ pa, pn, am: String(am), cu: "INR", tn: `HCS Demo Class Fee` }).toString();
  return `upi://pay?${q}`;
}

function qrUrl(intent) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(intent)}`;
}

const readFileAsDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function PayDemoPage() {
  const [params] = useSearchParams();
  const preselectedCourse = params.get("course") || "";
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({ upi_handle: "", payee_name: "HCS Computers AI", amount_inr: 500 });
  const [courses, setCourses] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [screenshotName, setScreenshotName] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    education: "",
    experience: "",
    course_interest: preselectedCourse,
    demo_date: "",
    upi_reference: "",
    payer_upi_id: "",
    screenshot_base64: "",
    screenshot_filename: "",
    notes: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("upi"); // "upi" | "paypal"

  useEffect(() => {
    api.get("/payment-config").then((r) => setConfig(r.data)).catch(() => {});
    fetchCourses().then(setCourses).catch(() => setCourses([]));
    api.get("/demo-dates").then((r) => setDates(r.data)).catch(() => setDates([]));
  }, []);

  const upi = useMemo(() => upiIntentUrl({ pa: config.upi_handle, pn: config.payee_name, am: config.amount_inr }), [config]);
  const qr = useMemo(() => qrUrl(upi), [upi]);
  const paypalQr = useMemo(() => (config.paypal_link ? qrUrl(config.paypal_link) : ""), [config.paypal_link]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target?.value ?? v }));

  const copyHandle = async () => {
    try {
      await navigator.clipboard.writeText(config.upi_handle);
      setCopied(true);
      toast.success("UPI handle copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const goToPayment = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.education || !form.experience || !form.course_interest) {
      toast.error("Please fill all required fields to continue.");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Screenshot must be under 3 MB");
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setForm((f) => ({ ...f, screenshot_base64: dataUrl, screenshot_filename: file.name }));
    setScreenshotName(file.name);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.upi_reference || form.upi_reference.trim().length < 6) {
      toast.error(paymentMethod === "paypal" ? "Please enter the PayPal Transaction ID." : "Please enter the UPI transaction reference (UTR).");
      return;
    }
    setLoading(true);
    const chosen = dates.find((d) => d.date === form.demo_date);
    const payload = {
      ...form,
      demo_date: chosen ? `${chosen.label} (${chosen.time_ist})` : form.demo_date || undefined,
      payment_method: paymentMethod,
      amount_currency: paymentMethod === "paypal" ? "USD" : "INR",
      amount_value: paymentMethod === "paypal" ? config.amount_usd : config.amount_inr,
    };
    try {
      await api.post("/demo-payment", payload);
      setSubmitted(true);
      toast.success("Payment recorded — we'll verify and confirm your slot shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="pay-success" className="mx-auto max-w-3xl px-6 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#0052FF]" />
        <h1 className="mt-8 font-display font-bold tracking-tighter text-4xl md:text-5xl text-slate-950">
          Payment received. Thank you, {form.full_name.split(" ")[0]}.
        </h1>
        <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
          We&apos;ve emailed all your details along with the UPI reference <span className="font-mono font-medium">{form.upi_reference}</span> to
          <span className="font-medium text-slate-900"> {COMPANY.email}</span>. Gurudeva will verify the payment in the Paytm/bank app and
          call you at <span className="font-medium text-slate-900">{form.phone}</span> to confirm your demo slot within 24 hours.
        </p>
        <div className="mt-10">
          <Link to="/" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 hover:bg-slate-700 transition-colors">
            Back to home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pay-demo-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 md:py-20">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Reserve your seat</div>
          <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl md:text-5xl lg:text-6xl text-slate-950 leading-[0.95] max-w-3xl">
            Pay ₹{config.amount_inr} <span className="text-slate-400">or</span> ${config.amount_usd} to book a demo class.
          </h1>
          <p className="mt-5 text-slate-600 text-base md:text-lg max-w-2xl">
            A refundable seat-hold — <b>₹{config.amount_inr}</b> via UPI (India) or <b>${config.amount_usd}</b> via PayPal (USA · UK · Australia).
            Once paid, we email your details to Gurudeva, verify the reference, and confirm your slot within 24 hours.
          </p>

          <div className="mt-8 flex items-center gap-3 text-xs font-mono-plex uppercase tracking-widerest">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 border ${step === 1 ? "bg-[#0052FF] text-white border-[#0052FF]" : "border-slate-300 text-slate-600"}`}>
              <span className="w-4 h-4 grid place-items-center bg-white/20">1</span> Your details
            </span>
            <span className="text-slate-400">›</span>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 border ${step === 2 ? "bg-[#0052FF] text-white border-[#0052FF]" : "border-slate-300 text-slate-600"}`}>
              <span className="w-4 h-4 grid place-items-center bg-white/20">2</span> Pay & confirm
            </span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12 md:py-16">
        {step === 1 && (
          <form onSubmit={goToPayment} data-testid="pay-step1-form" className="max-w-3xl border border-slate-200 p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="p_name">Full name*</Label>
                <Input id="p_name" data-testid="pay-input-name" required value={form.full_name} onChange={update("full_name")} className="mt-1.5 rounded-sm" placeholder="Your full name" />
              </div>
              <div>
                <Label htmlFor="p_email">Email*</Label>
                <Input id="p_email" data-testid="pay-input-email" type="email" required value={form.email} onChange={update("email")} className="mt-1.5 rounded-sm" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="p_phone">Phone (WhatsApp)*</Label>
                <Input id="p_phone" data-testid="pay-input-phone" required value={form.phone} onChange={update("phone")} className="mt-1.5 rounded-sm" placeholder="+91 98xxxxxxxx" />
              </div>
              <div>
                <Label>Education*</Label>
                <Select value={form.education} onValueChange={update("education")}>
                  <SelectTrigger data-testid="pay-select-education" className="mt-1.5 rounded-sm">
                    <SelectValue placeholder="Highest qualification" />
                  </SelectTrigger>
                  <SelectContent>
                    {EDUCATION.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Experience*</Label>
                <Select value={form.experience} onValueChange={update("experience")}>
                  <SelectTrigger data-testid="pay-select-experience" className="mt-1.5 rounded-sm">
                    <SelectValue placeholder="Where are you today?" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course for demo*</Label>
                <Select value={form.course_interest} onValueChange={update("course_interest")}>
                  <SelectTrigger data-testid="pay-select-course" className="mt-1.5 rounded-sm">
                    <SelectValue placeholder="Pick a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => <SelectItem key={c.id} value={c.title}>{c.title} ({c.vendor})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Preferred demo date</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {dates.slice(0, 6).map((d) => {
                    const selected = form.demo_date === d.date;
                    return (
                      <button
                        type="button"
                        key={d.date}
                        onClick={() => update("demo_date")(d.date)}
                        data-testid={`pay-date-${d.date}`}
                        className={`text-left px-3 py-2 text-sm border transition-all ${selected ? "border-[#0052FF] bg-[#0052FF]/5 text-slate-950" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}
                      >
                        <div className="font-medium">{d.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{d.time_ist} · {d.slot}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <button
              type="submit"
              data-testid="pay-continue-btn"
              className="mt-8 inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium px-8 py-3.5 transition-colors"
            >
              Continue to payment <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {step === 2 && (
          <div>
            <div className="mb-6 flex flex-wrap gap-2 border-b border-slate-200">
              <button
                type="button"
                onClick={() => setPaymentMethod("upi")}
                data-testid="pay-method-upi"
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  paymentMethod === "upi"
                    ? "border-[#0052FF] text-slate-950"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                🇮🇳 India · UPI · ₹{config.amount_inr}
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("paypal")}
                data-testid="pay-method-paypal"
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  paymentMethod === "paypal"
                    ? "border-[#0052FF] text-slate-950"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                🌍 USA / UK / Australia · PayPal · ${config.amount_usd}
              </button>
            </div>

          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              {paymentMethod === "upi" ? (
              <div className="border border-slate-200 p-6 md:p-8">
                <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Pay via UPI (India)</div>
                <div className="mt-2 font-display font-bold text-3xl text-slate-950">₹{config.amount_inr}</div>
                <div className="text-xs text-slate-500 mt-1">Demo class fee · {form.course_interest}</div>

                <div className="mt-6 flex flex-col items-center gap-4 border border-slate-200 p-5 bg-slate-50">
                  <img src={qr} alt="UPI QR" className="w-[240px] h-[240px] bg-white border border-slate-200" data-testid="pay-qr" />
                  <a
                    href={upi}
                    className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white text-sm font-medium px-4 py-2"
                    data-testid="pay-open-upi-app"
                  >
                    Open in UPI app
                  </a>
                </div>

                <div className="mt-6">
                  <Label className="text-xs uppercase tracking-widerest font-mono-plex text-slate-500">UPI Handle</Label>
                  <div className="mt-2 flex items-center border border-slate-300 bg-white">
                    <div className="flex-1 px-3 py-2.5 font-mono-plex text-sm text-slate-900 select-all" data-testid="pay-upi-handle">
                      {config.upi_handle}
                    </div>
                    <button
                      type="button"
                      onClick={copyHandle}
                      data-testid="pay-copy-handle"
                      className="px-3 py-2.5 border-l border-slate-300 hover:bg-slate-50 text-slate-700"
                      aria-label="Copy UPI handle"
                    >
                      {copied ? <CheckCircle2 className="w-4 h-4 text-[#0052FF]" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Payee: {config.payee_name}</p>
                </div>

                <ol className="mt-6 space-y-2 text-sm text-slate-700 list-decimal list-inside">
                  <li>Scan the QR or tap <b>Open in UPI app</b>.</li>
                  <li>Pay <b>₹{config.amount_inr}</b> to <b>{config.upi_handle}</b>.</li>
                  <li>Copy the 12-digit <b>UPI reference (UTR)</b> from your app.</li>
                  <li>Paste it in the form → we email the team instantly.</li>
                </ol>

                <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 border-t border-slate-100 pt-4">
                  <ShieldCheck className="w-4 h-4 mt-0.5 text-[#0052FF] shrink-0" />
                  Your payment is manually verified in our Paytm/bank app before the demo slot is confirmed.
                </div>
              </div>
              ) : (
              <div className="border border-slate-200 p-6 md:p-8">
                <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Pay via PayPal (International)</div>
                <div className="mt-2 font-display font-bold text-3xl text-slate-950">${config.amount_usd}</div>
                <div className="text-xs text-slate-500 mt-1">Demo class fee · {form.course_interest}</div>

                <div className="mt-6 flex flex-col items-center gap-4 border border-slate-200 p-6 bg-slate-50">
                  <div className="w-full text-center">
                    <div className="inline-flex items-center gap-2 font-display font-bold text-2xl text-[#003087]">
                      <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-600">
                      Scan the QR from your phone, or tap the button on desktop.
                    </p>
                  </div>
                  {paypalQr && (
                    <img
                      src={paypalQr}
                      alt="PayPal QR"
                      className="w-[220px] h-[220px] bg-white border border-slate-200"
                      data-testid="pay-paypal-qr"
                    />
                  )}
                  {config.paypal_link ? (
                    <a
                      href={config.paypal_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FFC439] hover:bg-[#FFB300] text-[#003087] text-sm font-bold px-6 py-3 transition-colors"
                      data-testid="pay-open-paypal"
                    >
                      Pay ${config.amount_usd} with PayPal <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <div className="text-xs text-slate-500">PayPal link not configured yet — please use UPI or contact us.</div>
                  )}
                </div>

                <ol className="mt-6 space-y-2 text-sm text-slate-700 list-decimal list-inside">
                  <li><b>Mobile:</b> scan the QR code above with your camera / any UPI or PayPal app.</li>
                  <li><b>Desktop:</b> tap <b>Pay ${config.amount_usd} with PayPal</b> to open the secure checkout.</li>
                  <li>Pay <b>${config.amount_usd}</b> using any credit/debit card or PayPal balance.</li>
                  <li>Copy the <b>PayPal Transaction ID</b> from the confirmation screen (looks like <span className="font-mono-plex">7XY12345AB678901C</span>).</li>
                  <li>Paste it in the form → we email the team instantly.</li>
                </ol>

                <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 border-t border-slate-100 pt-4">
                  <ShieldCheck className="w-4 h-4 mt-0.5 text-[#0052FF] shrink-0" />
                  Payment is verified in our PayPal account before the demo slot is confirmed.
                </div>
              </div>
              )}
            </div>

            <div className="lg:col-span-7">
              <form onSubmit={submit} data-testid="pay-step2-form" className="border border-slate-200 p-6 md:p-10">
                <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">After you pay</div>
                <h3 className="mt-2 font-display font-bold text-2xl md:text-3xl text-slate-950">Confirm your payment</h3>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Label htmlFor="p_utr">
                      {paymentMethod === "paypal" ? "PayPal Transaction ID*" : "UPI Transaction Reference (UTR)*"}
                    </Label>
                    <Input
                      id="p_utr"
                      data-testid="pay-input-utr"
                      required
                      value={form.upi_reference}
                      onChange={update("upi_reference")}
                      className="mt-1.5 rounded-sm font-mono-plex"
                      placeholder={paymentMethod === "paypal" ? "e.g. 7XY12345AB678901C" : "12-digit UTR from your UPI app"}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {paymentMethod === "paypal"
                        ? "Look for \u201cTransaction ID\u201d on the PayPal confirmation email or receipt."
                        : "Look for \u201cTransaction reference\u201d or \u201cUTR\u201d in your GPay/PhonePe/Paytm history."}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="p_upiid">
                      {paymentMethod === "paypal" ? "Your PayPal email (optional)" : "Your UPI ID (optional)"}
                    </Label>
                    <Input
                      id="p_upiid"
                      data-testid="pay-input-payer-upi"
                      value={form.payer_upi_id}
                      onChange={update("payer_upi_id")}
                      className="mt-1.5 rounded-sm"
                      placeholder={paymentMethod === "paypal" ? "you@paypal.com" : "e.g. name@okhdfcbank"}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Payment screenshot (optional)</Label>
                    <label htmlFor="p_shot" className="mt-1.5 flex items-center justify-between gap-3 border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-slate-500 transition-colors" data-testid="pay-upload-area">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Upload className="w-4 h-4" />
                        {screenshotName || "Upload payment screenshot (jpg/png, ≤3 MB)"}
                      </div>
                      {form.screenshot_base64 && <CheckCircle2 className="w-4 h-4 text-[#0052FF]" />}
                      <input id="p_shot" data-testid="pay-input-screenshot" type="file" accept="image/*" onChange={handleScreenshot} className="hidden" />
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="p_notes">Anything we should know? (optional)</Label>
                    <Textarea id="p_notes" data-testid="pay-input-notes" rows={2} value={form.notes} onChange={update("notes")} className="mt-1.5 rounded-sm" placeholder="Career goals, preferred trainer, etc." />
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3 items-center">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    data-testid="pay-back-btn"
                    className="text-sm text-slate-700 hover:text-slate-950 px-4 py-3 border border-slate-300"
                  >
                    ← Edit details
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="pay-submit-btn"
                    className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-medium px-8 py-3.5 transition-colors"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? "Sending…" : "Submit & email the team"}
                  </button>
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Details will be emailed to {COMPANY.email}. Payment is only confirmed after manual verification.
                </p>
              </form>
            </div>
          </div>
          </div>
        )}
      </section>
    </div>
  );
}
